import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import Form from '../../../../../components/Form';
import Checkbox from '../../../../../components/Checkbox';
import messages from '../messages';

import '../../styles.css';

export const getSystemsFromValue = (value) => (value ? JSON.parse(value) : []);

export const getStatesBySystems = (systems, options) =>
  systems.reduce((acc, system) => {
    const stateOption = options.find(({ raw }) => system.name in raw);
    const isStateAlreadyAdded = stateOption && acc.find(({ state }) => state === stateOption.value);
    if (stateOption && !isStateAlreadyAdded) {
      acc.push({
        state: stateOption.value,
        systems: Object.keys(stateOption.raw).map((key) => ({
          name: key,
          description: stateOption.raw[key],
        })),
      });
    }
    return acc;
  }, []);

export const getSystemsByStates = (states, options) =>
  states.map((state) => {
    const { raw } = options.find(({ value }) => value === state);
    return {
      state,
      systems: Object.keys(raw).map((key) => ({
        name: key,
        description: raw[key],
      })),
    };
  });

export const getValueFromSelectedSystems = (selectedSystems, options) => {
  const systems = selectedSystems.map((system) => {
    const found = options.find(({ raw }) => system.name in raw);
    /* istanbul ignore else */
    if (found) {
      return system;
    }
    return null;
  });
  return JSON.stringify(systems);
};

export const cleanStateSystems = (checkedSystems, selectedStates, options) =>
  checkedSystems.filter((system) => {
    const found = options.find(({ raw }) => system.name in raw);
    return found && selectedStates.includes(found.value);
  });

const ArticulationAgreementSystems = ({
  onChange,
  initialValue,
  formatMessage,
}) => {
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedSystemsByState, setSelectedSystemsByState] = useState([]);
  const [checkedSystems, setCheckedSystems] = useState([]);
  const states = sortBy(
    initialValue.options.map(({ value }) => ({
      value,
      text: value,
    })),
    'value',
  );
  const initialSystemsValue = getSystemsFromValue(initialValue.value);
  const initialStatesWithSystems = getStatesBySystems(
    initialSystemsValue,
    initialValue.options,
  );
  const initialStatesValue = initialStatesWithSystems.map(({ state }) => state);

  useEffect(() => {
    setSelectedStates(initialStatesValue);
    setSelectedSystemsByState(initialStatesWithSystems);
    setCheckedSystems(initialSystemsValue);
  }, []);

  return (
    <div styleName="articulation-agreements-systems-container" data-cy="Premium-Hem-articulationAgreements-Systems-Container">
      <p styleName="transfer-info-intro">{formatMessage(messages.articulationAgreementSystemsIntro)}</p>
      <Form.Select
        fluid
        lazyLoad
        search
        selection
        multiple
        options={states}
        value={selectedStates}
        data-cy="Premium-Hem-articulationAgreements-State-Dropdown"
        onChange={(e, { value }) => {
          setSelectedStates(value);
          const newSystems = getSystemsByStates(
            value,
            initialValue.options,
            initialSystemsValue,
          );
          setSelectedSystemsByState(newSystems);
          const newCheckedSystems = cleanStateSystems(
            checkedSystems,
            value,
            initialValue.options,
          );
          if (!isEqual(checkedSystems, newCheckedSystems)) {
            setCheckedSystems(newCheckedSystems);
            const newValue = getValueFromSelectedSystems(newCheckedSystems, initialValue.options);
            onChange(e, { value: newValue });
          }
        }}
      />
      {selectedSystemsByState.map((item) => (
        <div key={`artic_sys_state_${camelCase(item.state)}`} styleName="articulation-agreements-state-systems" data-cy="Premium-Hem-articulationAgreements-State-Systems-Container">
          <p styleName="articulation-agreements-systems-state">{item.state}</p>
          {item.systems.map((system) => {
            const key = `artic_sys_${camelCase(system.name)}`;
            const id = `artic_sys_checkbox_${camelCase(system.name)}`;
            return (
              <Form.Field key={key}>
                <Checkbox
                  id={id}
                  label={{
                    htmlFor: id,
                    children: system.description,
                  }}
                  checked={checkedSystems.map(({ name }) => name).includes(system.name)}
                  onChange={(e) => {
                    const newCheckedSystems = e.target.checked ?
                      [...checkedSystems, system] :
                      checkedSystems.filter(({ name }) => name !== system.name);
                    setCheckedSystems(newCheckedSystems);
                    const newValue = getValueFromSelectedSystems(newCheckedSystems, initialValue.options);
                    onChange(e, { value: newValue });
                  }}
                />
              </Form.Field>
            );
          })}
        </div>
      ))}
    </div>
  );
};
ArticulationAgreementSystems.propTypes = {
  onChange: PropTypes.func,
  formatMessage: PropTypes.func,
  initialValue: PropTypes.object,
};

export default ArticulationAgreementSystems;
